<template>
  <v-layout align-center>
    <v-flex xs12 sm10 md8 lg6>
      <v-layout wrap>
        <v-flex xs12>
          <div v-if="$apollo.loading">
            <spinner />
          </div>
          <form v-else v-scroll-to-error class="form-container" @submit.prevent="submitForm()">
            <div class="main-title">
              <span>{{ workflowScreenTitle(screenTitle) }}</span>
            </div>
            <accreditation-form
              :event="event"
              :initial-values="form"
              :vs="$v"
              :fields="schema.attendeeFields"
              create-layout
              @values="form = $event"
            />
            <div
              v-if="event.flags && event.flags.timeRestrictedAttendeeEntrance"
              style="display: flex; gap: 8px"
            >
              <datetime-field
                v-model="form.entryFrom"
                :label="adminText('workflowsAll.entry_from_2ea1c29', 'Entry from*')"
                style="width: 100%"
                :max="event.to.toString()"
              />
              <datetime-field
                v-model="form.entryTo"
                style="width: 100%"
                :label="adminText('workflowsAll.entry_to_f6ccf63', 'Entry to*')"
                :min="event.from.toString()"
              />
            </div>
            <v-flex v-if="ticketOptions.length" class="ticket-select-container">
              <v-select
                v-model="selectedTicketId"
                :items="workflowUi(ticketOptions)"
                :label="adminText('workflowsAll.select_ticket_eee0c42', 'Select ticket')"
                item-text="label"
                item-value="id"
                box
                clearable
                :error-messages="ticketSelectError ? [workflowLabel(ticketSelectError)] : []"
                :hint="adminText('workflowsAll.select_the_ticket_type_to_assign_to_the_atten_f8090c8', 'Select the ticket type to assign to the attendee with a full (100%) discount. This will be reflected in the statistics as a free ticket.')"
                persistent-hint
              />
            </v-flex>
            <div class="buttons-container">
              <main-button :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" flat @click.prevent="goBack" />
              <div class="lower-buttons">
                <main-button
                  v-if="showPrintAction"
                  :text="adminText('workflowsAll.create_print_09e9252', 'Create & Print')"
                  depressed
                  outline
                  flat
                  type="button"
                  @click.prevent="print"
                />
                <v-tooltip v-else-if="showDisabledPrintAction" top color="#979797">
                  <main-button slot="activator" :text="adminText('workflowsAll.create_print_09e9252', 'Create & Print')" disabled type="button" />
                  <span>
                    {{ adminText('workflowsAll.before_printing_the_accreditations_you_need_t_794b347', 'Before printing the accreditations, you need to create a template for them in Design Tool.') }}
                  </span>
                </v-tooltip>
                <div class="create-actions" :class="{ 'create-actions--split': hasTicketActions }">
                  <main-button
                    class="create-submit-button"
                    :text="adminText('workflowsAll.create_6e157c5', 'Create')"
                    depressed
                    type="button"
                    @click.prevent="submitForm()"
                  />
                  <v-menu
                    v-if="hasTicketActions"
                    offset-y
                    nudge-bottom="8"
                    nudge-left="165"
                    content-class="create-ticket-menu-content"
                  >
                    <v-btn
                      slot="activator"
                      color="primary"
                      depressed
                      type="button"
                      class="create-menu-button"
                      :aria-label="adminText('workflowsAll.open_create_actions_6538f2e', 'Open create actions')"
                    >
                      <v-icon color="white" class="create-menu-icon">arrow_drop_down</v-icon>
                    </v-btn>
                    <v-list class="create-menu-list">
                      <v-list-tile
                        class="create-menu-item"
                        @click="submitForm({ sendTicket: true })"
                      >
                        <v-list-tile-action>
                          <v-icon color="primary">mail_outline</v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>{{ adminText('workflowsAll.create_and_send_ticket_92387d0', 'Create and send ticket') }}</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </div>
          </form>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import AccreditationForm from './accreditation-form.vue'
import { createAttendee, createAttendeeTicketAssignment } from './../../http'
import { GET_EVENT_CATEGORIES } from '../../graphql/event.graphql'
import { GET_ORDER_ITEM_TICKET_OPTIONS } from '../../graphql/orderItem.graphql'
import MainButton from './../shared/main-button'
import { frontendURL, submitGuard, createAttendeeSchema } from './../../utils'
import Spinner from '../shared/spinner.vue'

import { requiredIf } from 'vuelidate/lib/validators'

export default {
  mixins: [WorkflowDisplay],
  name: 'create-accreditation',
  components: {
    AccreditationForm,
    MainButton,
    Spinner,
  },
  props: {
    organizationSlug: String,
    eventId: {
      default: null,
      type: String,
    },
    screenTitle: {
      default: 'New Accreditation',
      type: String,
    },
    hiddenCustomField: {
      default: () => {},
      type: Object,
    },
  },
  data: () => ({
    event: {},
    form: {},
    orderItems: [],
    selectedTicketId: null,
    ticketSelectError: null,
  }),
  validations() {
    return {
      form: {
        category: { required: requiredIf(() => this.event.extraModules.categoriesAndZones) },
        zones: { required: requiredIf(() => this.event.extraModules.categoriesAndZones) },
        ...this.schema.validationSchema,
      },
    }
  },
  apollo: {
    getEventCategories: {
      query: GET_EVENT_CATEGORIES,
      variables() {
        return {
          id: this.eventId,
        }
      },
      manual: true,
      result({ data: { getEventCategories: event }, loading }) {
        if (!loading) {
          this.event = event
          this.schema = createAttendeeSchema(this.event.attendeeFields, {})
        }
      },
    },
    getOrderItems: {
      query: GET_ORDER_ITEM_TICKET_OPTIONS,
      variables() {
        return {
          eventId: this.eventId,
          pagination: {
            page: 1,
            per_page: -1,
          },
          isGratis: false,
        }
      },
      manual: true,
      result({ data: { getOrderItems }, loading }) {
        if (!loading) {
          this.orderItems = (getOrderItems && getOrderItems.items) || []
        }
      },
    },
  },
  computed: {
    designSet() {
      return !!this.event.builder
    },
    showPrintAction() {
      return this.designSet && !this.hasTicketActions
    },
    showDisabledPrintAction() {
      return !this.designSet && !this.hasTicketActions
    },
    hasTicketActions() {
      return this.ticketOptions.length > 0
    },
    ticketOptions() {
      return this.orderItems
        .filter(ticket => ticket.type === 'ticket' && !(ticket.itemMeta && ticket.itemMeta.accessCode))
        .map(ticket => ({
          ...ticket,
          label: this.ticketLabel(ticket),
        }))
    },
  },
  watch: {
    selectedTicketId(value) {
      if (value) this.ticketSelectError = null
    },
  },
  methods: {
    ticketLabel(ticket) {
      const name = ticket.name || 'Ticket'
      const currency = ticket.currency || this.event.currency || ''
      const price = ticket.price || ticket.price === 0 ? ` - ${currency} ${ticket.price}` : ''

      return `${name}${price}`
    },
    async createAttendee(values, sendTicket = false) {
      const { category: categoryId, zones, ...rest } = values
      const fields = {
        ...rest,
        ...this.hiddenCustomField,
      }

      if (this.selectedTicketId) {
        this.ticketSelectError = null

        return createAttendeeTicketAssignment(
          this.eventId,
          categoryId,
          zones,
          null,
          fields,
          this.selectedTicketId,
          sendTicket,
        )
      }

      return createAttendee(this.eventId, categoryId, zones, null, fields)
    },
    async submitForm(options = {}) {
      const sendTicket = options && options.sendTicket === true
      if (sendTicket && !this.selectedTicketId) {
        this.ticketSelectError = 'workflowsAll.select_ticket_before_sending_27cc812'

        return
      }

      await submitGuard(this, async () => {
        await this.createAttendee(this.form, sendTicket)
        this.goBack()
      })
    },
    async print() {
      await submitGuard(this, async () => {
        const { id: attendeeId } = await this.createAttendee(this.form)
        const url = `${frontendURL}/organizations/${this.organizationSlug}/events/${this.event.id}/accreditation/${attendeeId}/edit/print`
        window.open(url, '_blank')
      })
    },
  },
}
</script>

<style>
.main-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.4375rem;
}
.buttons-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.ticket-select-container {
  margin-bottom: 40px;
}
.lower-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}
.lower-buttons .main-button {
  margin-right: 0 !important;
}
.lower-buttons > .v-tooltip {
  display: inline-flex;
}
.create-actions {
  display: inline-flex;
  align-items: center;
}
.create-actions .main-button {
  margin-right: 0 !important;
}
.create-actions--split {
  gap: 2px;
  height: 48px;
  overflow: hidden;
  border-radius: 24px;
}
.create-actions--split .create-submit-button.main-button {
  min-width: 114px !important;
  height: 48px !important;
  padding: 0 24px !important;
  margin: 0 !important;
  border-radius: 0 !important;
  background-color: #0277bd !important;
  box-shadow: none !important;
  font-family: 'Raleway';
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0;
}
.create-menu-button {
  min-width: 48px !important;
  width: 48px;
  height: 48px;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 0 !important;
  background-color: #004c8c !important;
  box-shadow: none !important;
}
.create-menu-icon {
  font-size: 20px;
}
.create-ticket-menu-content {
  min-width: 262px;
  border-radius: 8px !important;
  box-shadow: 0 4px 16px rgba(51, 51, 51, 0.16) !important;
}
.create-ticket-menu-content .create-menu-list {
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
}
.create-ticket-menu-content .create-menu-item {
  height: 48px;
  padding: 0 12px;
  background-color: #ffffff;
}
.create-ticket-menu-content .v-list__tile__action {
  min-width: 24px;
  margin-right: 12px;
}
.create-ticket-menu-content .v-list__tile__content {
  min-width: 0;
}
.create-ticket-menu-content .create-menu-list .v-list__tile__title {
  color: #0277bd;
  font-family: 'Raleway';
  font-weight: 700;
  font-size: 16px;
  line-height: 1.2;
  letter-spacing: 0;
}
@media screen and (max-width: 480px) {
  .buttons-container {
    flex-direction: column;
  }
  .lower-buttons {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
  .create-actions {
    display: flex;
  }
  .create-actions .main-button {
    flex: 1;
  }
  .create-actions--split .create-submit-button.main-button {
    min-width: 0 !important;
  }
}
@media screen and (max-width: 800px) {
  .main-title {
    display: none;
  }
  .form-container {
    margin-top: 1.5rem;
  }
}
</style>
